import {effect, EffectRef, inject, Injectable, Signal} from '@angular/core';
import {LanguageService} from './language.service';
import {L10n} from '@syncfusion/ej2-base';
import {LocaleService} from './locale.service';
import {toSignal} from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class SyncfusionTranslationService {
  private languageService: LanguageService = inject(LanguageService);
  private localeService: LocaleService = inject(LocaleService);
  private languageLoadingState: Signal<number> = toSignal(this.languageService.loadingIndicator$, {initialValue: 0});

  private langEffect: EffectRef = effect(() => {
    const localeId = this.languageService.language$();
    const locale = this.localeService.getLocale();
    const isLoadingLanguage = this.languageLoadingState() > 0;
    const loadedLanguagesVersion = this.languageService.loadedLanguagesVersion$();

    void loadedLanguagesVersion;

    if (isLoadingLanguage || !this.languageService.hasLoadedLanguage(localeId)) {
      return;
    }
    this.setSyncFusionTranslation(localeId, locale);
  });

  public setSyncFusionTranslation(localeId?: string, locale?: string): void {
    if(!localeId) {
      localeId = this.languageService.language$();
    }
    if(!locale) {
      locale = this.localeService.getLocale();
    }

    const translations = 
    {
      'schedule': {
        'newEventDetail': 'New Event',
        'day': this.languageService.getTranslationByKey('DAY'),
        'week': this.languageService.getTranslationByKey('WEEK'),
        'workWeek': this.languageService.getTranslationByKey('WORK_WEEK'),
        'month': this.languageService.getTranslationByKey('MONTH'),
        'year': this.languageService.getTranslationByKey('YEAR'),
        'agenda': this.languageService.getTranslationByKey('AGENDA'),
        'weekAgenda': this.languageService.getTranslationByKey('WEEK_AGENDA'),
        'workWeekAgenda': this.languageService.getTranslationByKey('WORK_WEEK_AGENDA'),
        'monthAgenda': this.languageService.getTranslationByKey('MONTH_AGENDA'),
        'today': this.languageService.getTranslationByKey('TODAY'),
        'noEvents': this.languageService.getTranslationByKey('NO_EVENTS'),
        'emptyContainer': this.languageService.getTranslationByKey('EMPTY_CONTAINER'),
        'allDay': this.languageService.getTranslationByKey('ALL_DAY'),
        'start': this.languageService.getTranslationByKey('START'),
        'end': this.languageService.getTranslationByKey('END'),
        'more': this.languageService.getTranslationByKey('MORE'),
        'close': this.languageService.getTranslationByKey('CLOSE'),
        'cancel': this.languageService.getTranslationByKey('CANCEL'),
        'noTitle': this.languageService.getTranslationByKey('NO_TITLE'),
        'delete': this.languageService.getTranslationByKey('DELETE'),
        'deleteEvent': this.languageService.getTranslationByKey('DELETE_EVENT'),
        'deleteMultipleEvent': this.languageService.getTranslationByKey('DELETE_MULTIPLE_EVENT'),
        'selectedItems': this.languageService.getTranslationByKey('SELECTED_ITEMS'),
        'deleteSeries': this.languageService.getTranslationByKey('DELETE_SERIES'),
        'edit': this.languageService.getTranslationByKey('EDIT'),
        'editSeries': this.languageService.getTranslationByKey('EDIT_SERIES'),
        'editEvent': this.languageService.getTranslationByKey('EDIT_EVENT'),
        'createEvent': this.languageService.getTranslationByKey('CREATE_EVENT'),
        'subject': this.languageService.getTranslationByKey('SUBJECT'),
        'addTitle': this.languageService.getTranslationByKey('ADD_TITLE'),
        'moreDetails': this.languageService.getTranslationByKey('MORE_DETAILS'),
        'moreEvents': this.languageService.getTranslationByKey('MORE_EVENTS'),
        'save': this.languageService.getTranslationByKey('SAVE'),
        'editContent': this.languageService.getTranslationByKey('EDIT_CONTENT'),
        'deleteContent': this.languageService.getTranslationByKey('DELETE_CONTENT'),
        'deleteMultipleContent': this.languageService.getTranslationByKey('DELETE_MULTIPLE_CONTENT'),
        'newEvent': this.languageService.getTranslationByKey('NEW_EVENT'),
        'title': this.languageService.getTranslationByKey('TITLE'),
        'location': this.languageService.getTranslationByKey('LOCATION'),
        'description': this.languageService.getTranslationByKey('DESCRIPTION'),
        'timezone': this.languageService.getTranslationByKey('TIMEZONE'),
        'startTimezone': this.languageService.getTranslationByKey('START_TIMEZONE'),
        'endTimezone': this.languageService.getTranslationByKey('END_TIMEZONE'),
        'repeat': this.languageService.getTranslationByKey('REPEAT'),
        'saveButton': this.languageService.getTranslationByKey('SAVE_BUTTON'),
        'cancelButton': this.languageService.getTranslationByKey('CANCEL_BUTTON'),
        'deleteButton': this.languageService.getTranslationByKey('DELETE_BUTTON'),
        'recurrence': this.languageService.getTranslationByKey('RECURRENCE'),
        'wrongPattern': this.languageService.getTranslationByKey('WRONG_PATTERN'),
        'seriesChangeAlert': this.languageService.getTranslationByKey('SERIES_CHANGE_ALERT'),
        'createError': this.languageService.getTranslationByKey('CREATE_ERROR'),
        'recurrenceDateValidation': this.languageService.getTranslationByKey('RECURRENCE_DATE_VALIDATION'),
        'sameDayAlert': this.languageService.getTranslationByKey('SAME_DAY_ALERT'),
        'occurenceAlert': this.languageService.getTranslationByKey('OCCURENCE_ALERT'),
        'editRecurrence': this.languageService.getTranslationByKey('EDIT_RECURRENCE'),
        'repeats': this.languageService.getTranslationByKey('REPEATS'),
        'alert': this.languageService.getTranslationByKey('ALERT'),
        'startEndError': this.languageService.getTranslationByKey('START_END_ERROR'),
        'invalidDateError': this.languageService.getTranslationByKey('INVALID_DATE_ERROR'),
        'blockAlert': this.languageService.getTranslationByKey('BLOCK_ALERT'),
        'ok': this.languageService.getTranslationByKey('OK'),
        'yes': this.languageService.getTranslationByKey('YES'),
        'no': this.languageService.getTranslationByKey('NO'),
        'occurrence': this.languageService.getTranslationByKey('OCCURRENCE'),
        'series': this.languageService.getTranslationByKey('SERIES'),
        'previous': this.languageService.getTranslationByKey('PREVIOUS'),
        'next': this.languageService.getTranslationByKey('NEXT'),
        'timelineDay': this.languageService.getTranslationByKey('TIMELINE_DAY'),
        'timelineWeek': this.languageService.getTranslationByKey('TIMELINE_WEEK'),
        'timelineWorkWeek': this.languageService.getTranslationByKey('TIMELINE_WORK_WEEK'),
        'timelineMonth': this.languageService.getTranslationByKey('TIMELINE_MONTH'),
        'timelineYear': this.languageService.getTranslationByKey('TIMELINE_YEAR'),
        'editFollowingEvent': this.languageService.getTranslationByKey('EDIT_FOLLOWING_EVENT'),
        'deleteTitle': this.languageService.getTranslationByKey('DELETE_TITLE'),
        'editTitle': this.languageService.getTranslationByKey('EDIT_TITLE'),
        'beginFrom': this.languageService.getTranslationByKey('BEGIN_FROM'),
        'endAt': this.languageService.getTranslationByKey('END_AT'),
        'expandAllDaySection': this.languageService.getTranslationByKey('EXPAND_ALL_DAY_SECTION'),
        'collapseAllDaySection': this.languageService.getTranslationByKey('COLLAPSE_ALL_DAY_SECTION'),
        'searchTimezone': this.languageService.getTranslationByKey('SEARCH_TIMEZONE'),
        'noRecords': this.languageService.getTranslationByKey('NO_RECORDS'),
        'deleteRecurrenceContent': this.languageService.getTranslationByKey('DELETE_RECURRENCE_CONTENT'),
        'of': this.languageService.getTranslationByKey('OF')
      },
      'recurrenceeditor': {
        'none': this.languageService.getTranslationByKey('NONE'),
        'daily': this.languageService.getTranslationByKey('DAILY'),
        'weekly': this.languageService.getTranslationByKey('WEEKLY'),
        'monthly': this.languageService.getTranslationByKey('MONTHLY'),
        'month': this.languageService.getTranslationByKey('MONTH'),
        'yearly': this.languageService.getTranslationByKey('YEARLY'),
        'never': this.languageService.getTranslationByKey('NEVER'),
        'until': this.languageService.getTranslationByKey('UNTIL'),
        'count': this.languageService.getTranslationByKey('COUNT'),
        'first': this.languageService.getTranslationByKey('FIRST'),
        'second': this.languageService.getTranslationByKey('SECOND'),
        'third': this.languageService.getTranslationByKey('THIRD'),
        'fourth': this.languageService.getTranslationByKey('FOURTH'),
        'last': this.languageService.getTranslationByKey('LAST'),
        'repeat': this.languageService.getTranslationByKey('REPEAT'),
        'repeatEvery': this.languageService.getTranslationByKey('REPEAT_EVERY'),
        'on': this.languageService.getTranslationByKey('ON'),
        'end': this.languageService.getTranslationByKey('END'),
        'onDay': this.languageService.getTranslationByKey('ON_DAY'),
        'days': this.languageService.getTranslationByKey('DAYS'),
        'weeks': this.languageService.getTranslationByKey('WEEKS'),
        'months': this.languageService.getTranslationByKey('MONTHS'),
        'years': this.languageService.getTranslationByKey('YEARS'),
        'every': this.languageService.getTranslationByKey('EVERY'),
        'summaryTimes': this.languageService.getTranslationByKey('SUMMARY_TIMES'),
        'summaryOn': this.languageService.getTranslationByKey('SUMMARY_ON'),
        'summaryUntil': this.languageService.getTranslationByKey('SUMMARY_UNTIL'),
        'summaryRepeat': this.languageService.getTranslationByKey('SUMMARY_REPEAT'),
        'summaryDay': this.languageService.getTranslationByKey('SUMMARY_DAY'),
        'summaryWeek': this.languageService.getTranslationByKey('SUMMARY_WEEK'),
        'summaryMonth': this.languageService.getTranslationByKey('SUMMARY_MONTH'),
        'summaryYear': this.languageService.getTranslationByKey('SUMMARY_YEAR'),
        'monthWeek': this.languageService.getTranslationByKey('MONTH_WEEK'),
        'monthPosition': this.languageService.getTranslationByKey('MONTH_POSITION'),
        'monthExpander': this.languageService.getTranslationByKey('MONTH_EXPANDER'),
        'yearExpander': this.languageService.getTranslationByKey('YEAR_EXPANDER'),
        'repeatInterval': this.languageService.getTranslationByKey('REPEAT_INTERVAL')
      },
      'uploader': {
        'invalidMinFileSize' : this.languageService.getTranslationByKey('INVALID_MIN_FILE_SIZE'),
        'invalidMaxFileSize' : this.languageService.getTranslationByKey('INVALID_MAX_FILE_SIZE'),
        'invalidFileType' : this.languageService.getTranslationByKey('INVALID_FILE_TYPE'),
        'Browse'  : this.languageService.getTranslationByKey('BROWSE'),
        'Clear' : this.languageService.getTranslationByKey('CLEAR'),
        'Upload' : this.languageService.getTranslationByKey('UPLOAD'),
        'dropFilesHint' : this.languageService.getTranslationByKey('DROP_FILES_HINT'),
        'uploadFailedMessage' : this.languageService.getTranslationByKey('UPLOAD_FAILED_MESSAGE'),
        'uploadSuccessMessage' : this.languageService.getTranslationByKey('UPLOAD_SUCCESS_MESSAGE'),
        'removedSuccessMessage': this.languageService.getTranslationByKey('REMOVED_SUCCESS_MESSAGE'),
        'removedFailedMessage': this.languageService.getTranslationByKey('REMOVED_FAILED_MESSAGE'),
        'inProgress': this.languageService.getTranslationByKey('IN_PROGRESS'),
        'readyToUploadMessage': this.languageService.getTranslationByKey('READY_TO_UPLOAD_MESSAGE'),
        'remove': this.languageService.getTranslationByKey('REMOVE'),
        'cancel': this.languageService.getTranslationByKey('CANCEL'),
        'delete': this.languageService.getTranslationByKey('DELETE'),
        'totalFiles': this.languageService.getTranslationByKey('TOTAL_FILES'),
        'size': this.languageService.getTranslationByKey('SIZE')
      },
      'datepicker': {
        today: this.languageService.getTranslationByKey('TODAY'),
        placeholder: this.languageService.getTranslationByKey('DATEPICKER_PLACEHOLDER'),
      },
      'dialog': {
        'close': this.languageService.getTranslationByKey('CLOSE'),
      },
      'richtexteditor': {
        'emojiPicker': this.languageService.getTranslationByKey('RICHTEXTEMOJIPICKERTOOLTIP'),
        'bold': this.languageService.getTranslationByKey('RICHTEXTBOLDTOOLTIP'),
        'italic': this.languageService.getTranslationByKey('RICHTEXTITALICTOOLTIP'),
        'underline': this.languageService.getTranslationByKey('RICHTEXTUNDERLINETOOLTIP'),
        'alignments': this.languageService.getTranslationByKey('RICHTEXTALIGNMENTSTOOLTIP'),
        'orderedList': this.languageService.getTranslationByKey('RICHTEXTORDEREDLISTTOOLTIP'),
        'unorderedList': this.languageService.getTranslationByKey('RICHTEXTUNORDEREDLISTTOOLTIP'),
        'clearFormat': this.languageService.getTranslationByKey('RICHTEXTCLEARFORMATTOOLTIP')
      },
      'multi-select': {
        'noRecordsTemplate': this.languageService.getTranslationByKey('MULTISELECTNORECORDSFOUND'),
        'actionFailureTemplate': this.languageService.getTranslationByKey('MULTISELECTREQUESTFAILED'),
        'overflowCountTemplate': '+${count} ' + this.languageService.getTranslationByKey('MULTISELECTMORE'),
        'totalCountTemplate': '${count} ' + this.languageService.getTranslationByKey('MULTISELECTMULTIPLESELECTED')
      },
      'colorpicker': {
        'Apply': this.languageService.getTranslationByKey('COLORPICKERAPPLY'),
        'Cancel': this.languageService.getTranslationByKey('COLORPICKERCANCEL'),
        'ModeSwitcher': this.languageService.getTranslationByKey('COLORPICKERMODESWITCH')
      }
    };
    L10n.load({
      [localeId]: translations,
      [locale]: translations
    });
  }
}
